import axios, { AxiosInstance } from 'axios';
import { getErrorMessage } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

let errorCallback: ((message: string) => void) | null = null;

export const setApiErrorCallback = (callback: (message: string) => void) => {
  errorCallback = callback;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (errorCallback) {
      const message = getErrorMessage(error);
      errorCallback(message);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data: any) => apiClient.post('/auth/register', data),
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),
  logout: () => apiClient.post('/auth/logout'),
  getMe: () => apiClient.get('/auth/me'),
  updateProfile: (data: any) => apiClient.put('/auth/profile', data),
  forgotPassword: (email: string) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (data: { token: string; password: string; password_confirmation: string }) =>
    apiClient.post('/auth/reset-password', data),
};

export const jobAPI = {
  getPublished: (params?: any) => apiClient.get('/jobs/published', { params }),
  getCategories: () => apiClient.get('/jobs/categories'),
  submitJob: (data: any) => apiClient.post('/jobs', data),
  getMyJobs: () => apiClient.get('/jobs/my-jobs'),
  getJob: (id: number) => apiClient.get(`/jobs/${id}`),
  updateJob: (id: number, data: any) => apiClient.put(`/jobs/${id}`, data),
  deleteJob: (id: number) => apiClient.delete(`/jobs/${id}`),
};

export const inquiryAPI = {
  submitInquiry: (data: any) => apiClient.post('/inquiries/submit', data),
  getMyInquiries: () => apiClient.get('/inquiries/my-inquiries'),
  getAllInquiries: (params?: any) => apiClient.get('/inquiries', { params }),
  getPendingInquiries: () => apiClient.get('/inquiries/pending'),
  updateInquiry: (id: number, data: any) => apiClient.put(`/inquiries/${id}`, data),
  approveInquiry: (id: number, data?: any) => apiClient.put(`/inquiries/${id}/approve`, data),
  deleteInquiry: (id: number) => apiClient.delete(`/inquiries/${id}`),
};

export const contractorAPI = {
  getAvailableJobs: (params?: any) => apiClient.get('/contractor/available-jobs', { params }),
  getJobDetail: (jobId: number) => apiClient.get(`/contractor/available-jobs/${jobId}`),
  createCheckout: (jobId: number, payment_method: string) => apiClient.post(`/contractor/purchase/${jobId}/checkout`, { payment_method }),
  purchaseLead: (jobId: number, data: { stripe_payment_intent_id?: string; paypal_order_id?: string }) => apiClient.post(`/contractor/purchase/${jobId}`, data),
  getMyLeads: (params?: any) => apiClient.get('/contractor/leads', { params }),
  getProfile: () => apiClient.get('/contractor/profile'),
};

export const paymentAPI = {
  createPaymentIntent: (jobId: number, amount: number) =>
    apiClient.post('/payments/create-intent', { job_id: jobId, amount }),
  confirmPayment: (paymentIntentId: string, jobId: number) =>
    apiClient.post('/payments/confirm', { payment_intent_id: paymentIntentId, job_id: jobId }),
  getPaymentStatus: (paymentIntentId: string) =>
    apiClient.get(`/payments/${paymentIntentId}/status`),
  refundPayment: (stripePaymentIntentId: string) =>
    apiClient.post('/payments/refund', { stripe_payment_intent_id: stripePaymentIntentId }),
};

export const adminAPI = {
  getPendingJobs: () => apiClient.get('/jobs/pending'),
  getAllJobs: (params?: any) => apiClient.get('/jobs', { params }),
  getJob: (jobId: number) => apiClient.get(`/jobs/${jobId}`),
  createJob: (data: any) => apiClient.post('/admin/jobs', data),
  updateJob: (jobId: number, data: any) =>
    apiClient.put(`/jobs/${jobId}`, data),
  verifyJob: (jobId: number, data: any) =>
    apiClient.put(`/jobs/${jobId}/verify`, data),
  deleteJob: (jobId: number) => apiClient.delete(`/jobs/${jobId}`),
  getStatistics: () => apiClient.get('/admin/statistics'),
  getUsers: (params?: any) => apiClient.get('/admin/users', { params }),
  getCustomers: (params?: any) => apiClient.get('/admin/customers', { params }),
  updateUser: (userId: number, data: any) => apiClient.put(`/admin/users/${userId}`, data),
  updateUserRole: (userId: number, role: string) =>
    apiClient.put(`/admin/users/${userId}/role`, { role }),
  deactivateUser: (userId: number) => apiClient.delete(`/admin/users/${userId}`),
  getPayments: (params?: any) => apiClient.get('/admin/payments', { params }),
  getCallbacks: (params?: any) => apiClient.get('/admin/callbacks', { params }),
  updateCallbackStatus: (id: number, status: string) =>
    apiClient.put(`/admin/callbacks/${id}/status`, { status }),
  getInquiries: (params?: any) => apiClient.get('/admin/inquiries', { params }),
  getInquiry: (id: number) => apiClient.get(`/admin/inquiries/${id}`),
  createInquiry: (data: any) => apiClient.post('/admin/inquiries', data),
  updateInquiry: (id: number, data: any) => apiClient.put(`/admin/inquiries/${id}`, data),
  approveInquiry: (id: number, data: any) =>
    apiClient.put(`/admin/inquiries/${id}/approve`, data),
  getInquiryPurchases: (id: number) => apiClient.get(`/admin/inquiries/${id}/purchases`),
  getContractorPurchasedLeads: (userId: number) => apiClient.get(`/admin/users/${userId}/purchased-leads`),
  getSettings: () => apiClient.get('/admin/settings'),
  getSetting: (key: string) => apiClient.get(`/admin/settings/${key}`),
  setSetting: (key: string, value: string) =>
    apiClient.put(`/admin/settings/${key}`, { value }),
  setSettings: (settings: Record<string, string>) =>
    apiClient.put('/admin/settings', { settings }),
  uploadLogo: (data: string, mimeType: string) =>
    apiClient.post('/admin/upload/logo', { data, mimeType }),
  uploadFavicon: (data: string, mimeType: string) =>
    apiClient.post('/admin/upload/favicon', { data, mimeType }),
};

export const healthAPI = {
  check: () => apiClient.get('/health'),
};

export const contentAPI = {
  getAll: () => apiClient.get('/content'),
};

export const publicAPI = {
  getFormConfig: () => apiClient.get('/form-config'),
};

export default apiClient;
